import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { getRoleLandingPath, saveRoleSwitchContext } from '../utils/roleSwitchFlow';
import { useBackTarget } from '../hooks/useBackTarget';
import { clearPendingPartnerIntent } from '../utils/partnerIntent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const buttonStyle = {
  width: 280,
  maxWidth: '100%',
  padding: '14px 0',
  fontFamily: font,
  fontSize: 24,
  fontWeight: 400,
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: 9999,
  cursor: 'pointer',
  boxShadow: 'var(--premium-shadow-main)',
};

const deriveRoleFromPath = (path, fallbackRole) => {
  if (path?.startsWith('/guest/')) {
    return 'guest';
  }
  if (path?.startsWith('/host/')) {
    return 'host';
  }
  if (path?.startsWith('/venue/')) {
    return 'venue_owner';
  }
  if (path?.startsWith('/staff-admin/') || path?.startsWith('/staff-app/')) {
    return 'venue_member';
  }
  if (fallbackRole === 'staff') {
    return 'venue_member';
  }
  return fallbackRole || 'guest';
};

const storeSession = (payload) => {
  localStorage.setItem('token', payload.access_token);
  localStorage.setItem('user', JSON.stringify(payload.user));
};

export default function SwitchRolePage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [loading, setLoading] = useState(true);
  const [switchingRole, setSwitchingRole] = useState('');
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const currentAppRole = useMemo(() => deriveRoleFromPath(location.state?.fromPath, user.role), [location.state?.fromPath, user.role]);
  const fromPath = location.state?.fromPath || getRoleLandingPath(currentAppRole);
  const hasVenueMember = Array.isArray(user.available_roles) && user.available_roles.includes('venue_member');
  const hasAdmin = Array.isArray(user.available_roles) && user.available_roles.includes('admin');

  useEffect(() => {
    const refreshUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { headers });
        localStorage.setItem('user', JSON.stringify(response.data));
        setUser(response.data);
      } catch (error) {
        console.error('Failed to refresh user before role switch:', error);
      } finally {
        setLoading(false);
      }
    };

    refreshUser();
  }, [headers]);

  const openPendingToast = (roleLabel) => {
    toast.info(`${roleLabel} access is still waiting for admin approval.`);
  };

  const startHostFlow = () => {
    // User has picked a concrete partner path — drop the persisted
    // "partner picker still pending" flag so they don't get re-routed
    // to /switch-role next launch.
    clearPendingPartnerIntent();
    saveRoleSwitchContext({ sourceRole: currentAppRole, targetRole: 'host', returnPath: fromPath });
    navigate('/host-intro-1');
  };

  const startVenueFlow = () => {
    clearPendingPartnerIntent();
    const [firstName = '', ...restNames] = (user.name || '').split(' ');
    const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    sessionStorage.setItem('venueOnboarding', JSON.stringify({
      ...existing,
      isAddingVenue: true,
      isRoleSwitch: true,
      returnPath: fromPath,
      returnVenueAppId: null,
      firstName,
      lastName: restNames.join(' '),
      email: user.email || '',
    }));
    saveRoleSwitchContext({ sourceRole: currentAppRole, targetRole: 'venue_owner', returnPath: fromPath });
    navigate('/venue/intro');
  };

  const switchDirectly = async (targetRole, landingPath = getRoleLandingPath(targetRole), uiKey = targetRole) => {
    setSwitchingRole(uiKey);
    try {
      const response = await axios.post(`${API}/auth/switch-role`, { target_role: targetRole }, { headers });
      storeSession(response.data);
      window.location.href = landingPath;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to switch roles right now');
      setSwitchingRole('');
    }
  };

  const handleRoleTap = async (targetRole) => {
    // Give instant visual feedback the moment the user taps — some paths
    // (venue → staff /my-role fetch, admin unlock prefetch, host status
    // decisioning) do async work BEFORE `switchDirectly` sets this flag,
    // which previously left the button looking inert for 2-3 seconds.
    if (switchingRole) return;
    setSwitchingRole(targetRole);

    if (targetRole === 'guest') {
      // User is explicitly opting into the guest app — no longer in the
      // "partner picker pending" state.
      clearPendingPartnerIntent();
      await switchDirectly('guest');
      return;
    }

    if (targetRole === 'admin') {
      // Main admins (auto-unlocked) + already-unlocked promoted admins go
      // straight through. Promoted admins who haven't entered their
      // 4-digit code yet must do so via the unlock modal first.
      if (user.is_admin_unlocked || user.is_main_admin) {
        await switchDirectly('admin', '/admin');
      } else {
        setSwitchingRole('');
        setUnlockModalOpen(true);
      }
      return;
    }

    if (targetRole === 'host') {
      if (user.host_status === 'approved') {
        await switchDirectly('host');
      } else if (user.host_status === 'rejected') {
        await switchDirectly('host', '/host/listings');
      } else if (user.host_status === 'pending') {
        setSwitchingRole('');
        openPendingToast('Host');
      } else {
        startHostFlow();
      }
      return;
    }

    if (targetRole === 'venue') {
      // "Food/Drink Venue" and "Venue member" are merged into a single
      // choice — the user's OWN venue always takes priority (they can
      // reach their staff venue too, from the switcher inside the venue
      // app's top-left dropdown, once they're in). Only fall back to
      // landing directly on their staff venue when they don't own one.
      const hasOwnVenueAttempt = Boolean(user.venue_status);
      if (hasOwnVenueAttempt) {
        if (
          user.venue_status === 'approved'
          || user.venue_status === 'setup_mode'
          || user.venue_status === 'pending'
          || user.venue_status === 'submitted'
          || user.venue_status === 'on_hold'
        ) {
          // Pass 'venue' as the UI key so the spinner on the Venue button
          // keeps rendering while switchDirectly resolves (backend role
          // name 'venue_owner' doesn't match the button id).
          await switchDirectly('venue_owner', '/venue/analytics', 'venue');
        } else {
          startVenueFlow();
        }
        return;
      }

      if (hasVenueMember) {
        let landing = '/staff-app/scan';
        try {
          const staffHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
          const me = await axios.get(`${API}/venue/staff/my-role`, { headers: staffHeaders });
          if (me.data?.role === 'admin') landing = '/staff-admin/scan';
        } catch (_err) { /* default to regular staff app */ }
        await switchDirectly('venue_member', landing, 'venue');
        return;
      }

      startVenueFlow();
      return;
    }
  };

  const getRoleStatus = (targetRole) => {
    if (targetRole === 'guest') {
      return { label: 'Available', background: 'var(--status-success-surface)', color: 'var(--status-success-text)' };
    }
    if (targetRole === 'admin') {
      return { label: 'Admin access', background: 'rgba(229, 36, 69, 0.12)', color: '#B91C1C' };
    }

    if (targetRole === 'host') {
      if (user.host_status === 'approved') {
        return { label: 'Available', background: 'var(--status-success-surface)', color: 'var(--status-success-text)' };
      }
      if (user.host_status === 'rejected') {
        return { label: 'Limited access', background: 'rgba(217, 130, 43, 0.12)', color: '#B56B11' };
      }
      if (user.host_status === 'pending') {
        return { label: 'Pending approval', background: 'var(--status-warning-surface)', color: 'var(--status-warning-text)' };
      }
      return { label: 'Start setup', background: 'var(--accent-surface)', color: 'var(--accent-purple-strong)' };
    }

    // targetRole === 'venue' — status reflects the user's OWN venue first
    // (matches the priority used by `handleRoleTap`); if they don't have
    // one, fall back to their staff-membership availability.
    if (user.venue_status === 'approved' || user.venue_status === 'setup_mode') {
      return { label: 'Available', background: 'var(--status-success-surface)', color: 'var(--status-success-text)' };
    }
    if (user.venue_status === 'pending' || user.venue_status === 'submitted') {
      // Pending admin approval — owner CAN still enter their venue app and
      // keep editing photos/hours/vibe/socials. Banner on /venue/analytics
      // surfaces the "under review" state.
      return { label: 'Under review', background: 'var(--status-warning-surface)', color: 'var(--status-warning-text)' };
    }
    if (user.venue_status === 'on_hold') {
      return { label: 'On hold', background: 'var(--status-warning-surface)', color: 'var(--status-warning-text)' };
    }
    if (hasVenueMember) {
      return { label: 'Available', background: 'var(--status-success-surface)', color: 'var(--status-success-text)' };
    }
    return { label: 'Start setup', background: 'var(--accent-surface)', color: 'var(--accent-purple-strong)' };
  };

  const submitUnlockCode = async (event) => {
    if (event) event.preventDefault();
    if (!/^\d{4}$/.test(unlockCode)) {
      toast.error('Enter your 4-digit unlock code');
      return;
    }
    setUnlocking(true);
    try {
      await axios.post(`${API}/auth/admin-unlock`, { code: unlockCode.trim() }, { headers });
      toast.success('Admin unlocked');
      setUnlockModalOpen(false);
      setUnlockCode('');
      // Refresh user so is_admin_unlocked persists for next switch
      try {
        const me = await axios.get(`${API}/auth/me`, { headers });
        localStorage.setItem('user', JSON.stringify(me.data));
        setUser(me.data);
      } catch (_err) { /* non-fatal */ }
      await switchDirectly('admin', '/admin');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Wrong unlock code');
    } finally {
      setUnlocking(false);
    }
  };

  const buttons = [
    { id: 'guest', label: 'Guest Traveler' },
    { id: 'host', label: 'Airbnb Host' },
    { id: 'venue', label: 'Venue' },
    ...(hasAdmin ? [{ id: 'admin', label: 'Admin' }] : []),
  ].filter((button) => {
    if (button.id === 'venue') {
      return currentAppRole !== 'venue_owner' && currentAppRole !== 'venue_member';
    }
    return button.id !== currentAppRole;
  });

  return (
    <div className="app-shell" data-testid="switch-role-page" style={{ minHeight: '100vh', minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'var(--app-bg)', position: 'relative' }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'space-between', padding: '16px 20px 0' }}>
        <button onClick={goBack} data-testid="switch-role-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={1.5} />
        </button>
        <NotificationBellButton buttonTestId="switch-role-bell-button" popupTestId="switch-role-notification-popup" />
      </div>

      <RouteTransitionLogo width={160} height={143} dataTestId="switch-role-shared-logo" style={{ marginTop: 26 }} />
      <h1 style={{ fontFamily: font, fontSize: 28, fontWeight: 400, color: 'var(--text)', marginTop: 12 }}>Switch to..</h1>

      <div style={{ marginTop: 34, width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        {loading ? (
          <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text-2)' }}>Loading roles...</p>
        ) : buttons.map((button) => {
          const isSwitching = switchingRole === button.id;
          return (
          <React.Fragment key={button.id}>
            <button
              onClick={() => handleRoleTap(button.id)}
              data-testid={`switch-role-${button.id}-button`}
              style={{
                ...buttonStyle,
                opacity: switchingRole && !isSwitching ? 0.55 : 1,
                transform: isSwitching ? 'scale(0.985)' : 'scale(1)',
                transition: 'transform 160ms ease, opacity 160ms ease, box-shadow 160ms ease',
                boxShadow: isSwitching ? 'var(--premium-shadow-main), 0 0 0 2px var(--accent-purple-strong) inset' : 'var(--premium-shadow-main)',
                cursor: switchingRole ? 'wait' : 'pointer',
              }}
              disabled={Boolean(switchingRole)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  {isSwitching ? (
                    <Loader2
                      size={22}
                      color="var(--accent-purple-strong)"
                      strokeWidth={2.25}
                      className="animate-spin"
                      data-testid={`switch-role-${button.id}-spinner`}
                    />
                  ) : null}
                  <span>{isSwitching ? 'Switching…' : button.label}</span>
                </span>
                <span
                  data-testid={`switch-role-${button.id}-status`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 124,
                    padding: '4px 12px',
                    borderRadius: 9999,
                    background: getRoleStatus(button.id).background,
                    color: getRoleStatus(button.id).color,
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {getRoleStatus(button.id).label}
                </span>
              </div>
            </button>
          </React.Fragment>
          );
        })}
      </div>

      <div style={{ flex: 1 }} />

      {unlockModalOpen ? (
        <div
          data-testid="switch-role-admin-unlock-modal"
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}
          onClick={() => !unlocking && setUnlockModalOpen(false)}
        >
          <form
            onSubmit={submitUnlockCode}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 380, width: '100%', background: 'var(--surface)', borderRadius: 22, padding: 22, boxShadow: 'var(--premium-shadow-main)', display: 'grid', gap: 14 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--accent-surface)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldCheck size={20} color="var(--accent-purple-strong)" />
              </span>
              <div>
                <h3 style={{ fontFamily: font, fontSize: 18, margin: 0, color: 'var(--text)' }}>Unlock Admin</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-2)' }}>
                  Enter the 4-digit code the founder gave you. You only need to do this once.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 14, border: '1px solid var(--card-border)', background: 'var(--surface-2)' }}>
              <Lock size={16} color="var(--text-2)" />
              <input
                data-testid="switch-role-admin-unlock-input"
                autoFocus
                inputMode="numeric"
                maxLength={4}
                placeholder="••••"
                value={unlockCode}
                onChange={(e) => setUnlockCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 22, letterSpacing: 8, fontWeight: 700, color: 'var(--text)', textAlign: 'center' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="switch-role-admin-unlock-cancel"
                onClick={() => setUnlockModalOpen(false)}
                disabled={unlocking}
                style={{ padding: '8px 14px', borderRadius: 9999, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', fontWeight: 700, fontSize: 13, cursor: unlocking ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="switch-role-admin-unlock-confirm"
                disabled={unlocking || unlockCode.length !== 4}
                style={{ padding: '8px 16px', borderRadius: 9999, border: 'none', background: 'var(--accent-purple-strong)', color: '#FFF', fontWeight: 700, fontSize: 13, cursor: unlocking || unlockCode.length !== 4 ? 'not-allowed' : 'pointer', opacity: unlocking || unlockCode.length !== 4 ? 0.65 : 1 }}
              >
                {unlocking ? 'Unlocking…' : 'Unlock'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}